import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { paymentStatus } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { getPlayer } from "@/lib/api";
import { Button } from "@/components/ui/button";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 15;

export default function PaymentSuccess() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { player, setPlayer } = usePlayer();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: "pending", attempts: 0 });
  const stopped = useRef(false);

  useEffect(() => {
    if (!sessionId) { navigate("/parental"); return; }
    let attempts = 0;
    const poll = async () => {
      if (stopped.current) return;
      attempts += 1;
      try {
        const s = await paymentStatus(sessionId);
        setState({ status: s.status, payment_status: s.payment_status, attempts });
        if (s.payment_status === "paid") {
          stopped.current = true;
          if (player?.id) {
            try {
              const fresh = await getPlayer(player.id);
              setPlayer(fresh);
            } catch (e) { /* ignore */ }
          }
          return;
        }
        if (["failed", "expired", "refunded"].includes(s.payment_status)) {
          stopped.current = true;
          return;
        }
      } catch (e) { /* ignore transient */ }
      if (attempts >= MAX_ATTEMPTS) {
        stopped.current = true;
        setState((prev) => ({ ...prev, status: "timeout" }));
        return;
      }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => { stopped.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const paid = state.payment_status === "paid";
  const failed = ["failed", "expired", "refunded"].includes(state.payment_status);

  return (
    <div className="relative min-h-screen hero-radial grain flex items-center justify-center p-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="glass-strong rounded-3xl p-8 max-w-md w-full text-center"
      >
        {!paid && !failed && (
          <>
            <Loader2 className="w-14 h-14 text-amber-300 mx-auto animate-spin" />
            <h2 className="mt-4 font-serif text-3xl font-black text-stone-50">{t("payment.verifying_title")}</h2>
            <p className="mt-2 text-sm text-stone-400">{t("payment.verifying_text")}</p>
          </>
        )}
        {paid && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto shard-glow" />
            <div className="mt-3 text-xs uppercase tracking-[0.3em] text-emerald-300 font-bold">{t("payment.success_kicker")}</div>
            <h2 className="mt-2 font-serif text-3xl font-black text-stone-50">{t("payment.success_title")}</h2>
            <p className="mt-2 text-sm text-stone-300">{t("payment.success_text")}</p>
            <div className="mt-6 flex gap-3 justify-center">
              <Button onClick={() => navigate("/levels")} className="bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full px-6" data-testid="btn-continue-game">
                <Sparkles className="w-4 h-4 mr-2" /> {t("payment.continue_game")}
              </Button>
            </div>
          </>
        )}
        {failed && (
          <>
            <XCircle className="w-14 h-14 text-rose-400 mx-auto" />
            <h2 className="mt-3 font-serif text-3xl font-black text-stone-50">{t("payment.failed_title")}</h2>
            <p className="mt-2 text-sm text-stone-400">{t("payment.failed_text", { status: state.payment_status })}</p>
            <div className="mt-6">
              <Link to="/parental" className="text-amber-300 hover:text-amber-200 font-bold text-sm" data-testid="link-back-parental">{t("payment.back_to_parental")}</Link>
            </div>
          </>
        )}
        {state.status === "timeout" && !paid && (
          <div className="mt-4 text-xs text-stone-500">
            {t("payment.timeout_text")}
          </div>
        )}
      </motion.div>
    </div>
  );
}
